import { fetchSyllabus } from '@/fetchers'
import {
	Syllabus,
	SyllabusAssessmentStagecontent,
} from '@/kontent/content-types'
import CustomAccordion from '@/legacy-ported/components/custom/CustomAccordion'
import AssessmentStageContent from '@/legacy-ported/components/syllabus/AssessmentStageContent'
import { Mapping, TaxoStageWithLifeSkill } from '@/types'
import { getSyllabusElements } from '@/utils'
import {
	Elements,
	IContentItemsContainer,
	Responses,
} from '@kontent-ai/delivery-sdk'
import { useQuery } from '@tanstack/react-query'
import { Fragment, useState } from 'react'
import { Loading } from '../Loading'
import { getLinkedItems } from '../contexts/KontentHomeConfigProvider'
import { GridCol } from '../nsw/grid/GridCol'
import { GridWrapper } from '../nsw/grid/GridWrapper'
import { SideNav, SideNavItem } from '../nsw/side-nav/SideNav'

export interface SyllabusAssessmentProps {
	syllabus: Syllabus
	linkedItems: IContentItemsContainer
	mappings: Mapping[]
	currentStages: TaxoStageWithLifeSkill[]
	syllabusResponse?: Responses.IViewContentItemResponse<Syllabus>
	lazy?: boolean
	pageType?: string
}

type AssessmentSetType =
	| 'Course standards'
	| 'School-based assessment'
	| 'HSC examinations'

interface AssessmentSet {
	assessmentsInfo: Elements.RichTextElement
	assessmentStageContents?: Elements.LinkedItemsElement<SyllabusAssessmentStagecontent>
	type?: AssessmentSetType
}

const getAssessmentContents = (syllabus: Syllabus) => {
	const assessmentSets: AssessmentSet[] = [
		{
			assessmentsInfo: syllabus.elements.assessments_info,
			assessmentStageContents: syllabus.elements.assessments,
		},
		{
			assessmentsInfo: syllabus.elements.cs_assessments_info,
			assessmentStageContents: syllabus.elements.cs_assessments,
			type: 'Course standards',
		},
		{
			assessmentsInfo: syllabus.elements.sb_assessments_info,
			assessmentStageContents: syllabus.elements.sb_assessments,
			type: 'School-based assessment',
		},
		{
			assessmentsInfo: syllabus.elements.hsc_assessments_info,
			assessmentStageContents: syllabus.elements.hsc_assessments,
			type: 'HSC examinations',
		},
	]
	return assessmentSets
}

const getStageContentFromAssessmentSet = (
	assessmentSet: AssessmentSet,
	currentStages: TaxoStageWithLifeSkill[],
	linkedItems: IContentItemsContainer,
) =>
	getLinkedItems(assessmentSet.assessmentStageContents, linkedItems)?.filter(
		(assessment) => {
			return assessment.elements.stages__stages.value?.some((stage) =>
				currentStages.includes(stage.codename),
			)
		},
	) || []

// /prepare in future: when "Assessment model" field is removed from CMS,
// default to 'v2' so Assessment UI mode drives rendering
export function getAssessmentModel(syllabus: Syllabus): 'v1' | 'v2' {
	const value = syllabus.elements.assessment_model?.value?.[0]?.codename
	return value === 'v1' ? 'v1' : 'v2'
}

// /prepare in future: when "Assessment model" field is removed,
// this field becomes the sole driver of assessment rendering.
// Defaults to 'multi_page_mode' if not set.
export function getAssessmentUiMode(
	syllabus: Syllabus,
): 'single_page_mode' | 'multi_page_mode' {
	const value = syllabus.elements.assessment_ui_mode?.value?.[0]?.codename
	if (value === 'single_page_mode') return 'single_page_mode'
	return 'multi_page_mode'
}

const AssessmentContentV1 = ({
	syllabus,
	linkedItems,
	mappings,
	currentStages,
	pageType,
}: {
	syllabus: Syllabus
	linkedItems: IContentItemsContainer
	mappings: Mapping[]
	currentStages: TaxoStageWithLifeSkill[]
	pageType?: string
}) => {
	const assessmentSets = getAssessmentContents(syllabus)

	return (
		<>
			{assessmentSets.map((assessmentSet) => {
				const sections = getStageContentFromAssessmentSet(
					assessmentSet,
					currentStages,
					linkedItems,
				)

				const isAccordion = assessmentSet.type && sections.length

				const Component = isAccordion ? CustomAccordion : Fragment

				return (
					<Component
						key={assessmentSet.type}
						title={isAccordion ? assessmentSet.type : undefined}
					>
						<AssessmentStageContent
							info={assessmentSet.assessmentsInfo}
							sections={sections}
							mappings={mappings}
							linkedItems={linkedItems}
							suffixForHeading={syllabus.system.codename}
							pageType={pageType}
						/>
					</Component>
				)
			})}
		</>
	)
}

const AssessmentContentV2 = ({
	syllabus,
	linkedItems: _linkedItems,
	mappings,
	currentStages,
	syllabusResponse,
	pageType,
}: {
	syllabus: Syllabus
	linkedItems: IContentItemsContainer
	mappings: Mapping[]
	currentStages: TaxoStageWithLifeSkill[]
	syllabusResponse: Responses.IViewContentItemResponse<Syllabus>
	pageType?: string
}) => {
	const assessmentPages = getLinkedItems(
		syllabus.elements.assessments_pages,
		syllabusResponse.linkedItems,
	)
	const [section, setSection] = useState<string | null>(
		assessmentPages[0].elements.slug.value,
	)

	const staticSideNavItems: SideNavItem[] = assessmentPages.map((item) => ({
		text: item.elements.title.value,
		href: '#',
		isActive: item.elements.slug.value === section,
		onClick: (e) => {
			e.preventDefault()
			setSection(item.elements.slug.value)
		},
	}))

	const currentSectionTitle = staticSideNavItems?.find(
		(item) => item.isActive,
	)?.text

	const assessmentPage = assessmentPages.find(
		(item) => item.elements.slug.value === section,
	)

	const assessmentStageContents =
		getLinkedItems(
			assessmentPage?.elements.assessments,
			syllabusResponse.linkedItems,
		)?.filter((item) => {
			return item.elements.stages__stages.value?.some((stage) =>
				currentStages.includes(stage.codename),
			)
		}) || []

	return (
		<>
			<GridWrapper className="-mt-8">
				<GridCol lg={3}>
					<SideNav className="block" items={staticSideNavItems} />
				</GridCol>
				<GridCol lg={9}>
					<h3 className="mb-8">{currentSectionTitle}</h3>
					<AssessmentStageContent
						info={assessmentPage?.elements.info}
						sections={assessmentStageContents}
						mappings={mappings}
						linkedItems={syllabusResponse.linkedItems}
						suffixForHeading={syllabus.system.codename}
						isUseNonFullWidthWrapper={false}
						sectionTitleTag={'h4'}
						assessmentGroupTitleTag={'h5'}
						contentAfterSections={assessmentPage?.elements.content}
						pageType={pageType}
					/>
				</GridCol>
			</GridWrapper>
		</>
	)
}

const AssessmentContentV2SinglePage = ({
	syllabus,
	mappings,
	currentStages,
	syllabusResponse,
	pageType,
}: {
	syllabus: Syllabus
	linkedItems: IContentItemsContainer
	mappings: Mapping[]
	currentStages: TaxoStageWithLifeSkill[]
	syllabusResponse: Responses.IViewContentItemResponse<Syllabus>
	pageType?: string
}) => {
	const assessmentPages = getLinkedItems(
		syllabus.elements.assessments_pages,
		syllabusResponse.linkedItems,
	)

	if (!assessmentPages || assessmentPages.length === 0) {
		return null
	}

	const assessmentPage = assessmentPages[0]

	const assessmentStageContents =
		getLinkedItems(
			assessmentPage.elements.assessments,
			syllabusResponse.linkedItems,
		)?.filter((item) => {
			return item.elements.stages__stages.value?.some((stage) =>
				currentStages.includes(stage.codename),
			)
		}) || []

	return (
		<AssessmentStageContent
			info={assessmentPage.elements.info}
			sections={assessmentStageContents}
			mappings={mappings}
			linkedItems={syllabusResponse.linkedItems}
			suffixForHeading={syllabus.system.codename}
			contentAfterSections={assessmentPage.elements.content}
			pageType={pageType}
		/>
	)
}

export const SyllabusAssessment = ({
	syllabus,
	linkedItems,
	mappings,
	currentStages,
	syllabusResponse: _syllabusResponse,
	lazy = true,
	pageType,
}: SyllabusAssessmentProps) => {
	const syllabusCodename = syllabus.system.codename

	const { data: syllabusResponse, isFetched } = useQuery(
		[
			`SyllabusAssessment${syllabusCodename + lazy ? 'lazy' : ''}`,
			syllabusCodename,
		],
		() => {
			return lazy
				? fetchSyllabus(
						syllabusCodename,
						getSyllabusElements(['assessment']),
						5,
				  )
				: _syllabusResponse
		},
		{
			staleTime: Infinity,
		},
	)

	if (!isFetched) return <Loading />

	const _linkedItems = {
		...linkedItems,
		...syllabusResponse.linkedItems,
	}

	const model = getAssessmentModel(syllabus)
	const uiMode = getAssessmentUiMode(syllabus)

	if (model === 'v1') {
		return (
			<AssessmentContentV1
				syllabus={syllabus}
				linkedItems={_linkedItems}
				mappings={mappings}
				currentStages={currentStages}
				pageType={pageType}
			/>
		)
	}

	if (uiMode === 'single_page_mode') {
		return (
			<AssessmentContentV2SinglePage
				syllabus={syllabus}
				linkedItems={_linkedItems}
				mappings={mappings}
				currentStages={currentStages}
				syllabusResponse={syllabusResponse}
				pageType={pageType}
			/>
		)
	}

	return (
		<AssessmentContentV2
			syllabus={syllabus}
			linkedItems={_linkedItems}
			mappings={mappings}
			currentStages={currentStages}
			syllabusResponse={syllabusResponse}
			pageType={pageType}
		/>
	)
}
